import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { format } from 'date-fns';
import { cpApi } from '../api/api';
import type { VndQrProfile } from '../types';
import { formatDashboardPercent } from './dashboard/formatters';

function confirmChannel(item: VndQrProfile): 'sms' | 'casso' | 'sepay' | 'none' {
  if (item.sms_enabled) return 'sms';
  if (item.work_mode !== 'auto') return 'none';
  return item.payment_provider === 'casso' ? 'casso' : 'sepay';
}

function channelLabel(item: VndQrProfile, manualLabel: string): string {
  const channel = confirmChannel(item);
  if (channel === 'sms') return 'SMS 🟡';
  if (channel === 'casso') return 'Casso 🟢';
  if (channel === 'sepay') return 'SePay 🔵';
  return manualLabel;
}

function enableLockedFromError(e: unknown): { message?: string; locked_by?: string } | null {
  if (!axios.isAxiosError(e) || e.response?.status !== 409) return null;
  const d = e.response.data as { error?: string; message?: string; locked_by?: string } | string | undefined;
  if (!d || typeof d === 'string') return null;
  if (d.error !== 'enable_locked') return null;
  return d;
}

function ymd(d: Date | null | undefined): string {
  return d ? format(d, 'yyyy-MM-dd') : '';
}

export default function VndRequestsProfileFilter({
  profiles,
  value,
  onChange,
  onProfilesChange,
  dateRange,
  isResponsible,
  userId,
}: {
  profiles: VndQrProfile[];
  value: string;
  onChange: (value: string) => void;
  onProfilesChange: (next: VndQrProfile[]) => void;
  dateRange?: { from: Date | null; to: Date | null };
  isResponsible: boolean;
  userId: number | null;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [conversionById, setConversionById] = useState<Record<number, number>>({});
  const [allConversion, setAllConversion] = useState<number | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [box, setBox] = useState<{ top: number; left: number; width: number } | null>(null);

  const selected = profiles.find((p) => String(p.id) === value);
  const triggerLabel = selected?.name || t('header.profileAll');

  const dateFrom = ymd(dateRange?.from);
  const dateTo = ymd(dateRange?.to);

  useEffect(() => {
    if (!dateFrom || !dateTo) {
      setConversionById({});
      setAllConversion(null);
      return;
    }
    let cancelled = false;
    void cpApi
      .getDashboardCharts({ dateFrom, dateTo, granularity: 'day' })
      .then((res) => {
        if (cancelled) return;
        const map: Record<number, number> = {};
        for (const p of res.profiles ?? []) {
          if ((p.count ?? 0) > 0) map[p.id] = Number(p.conversion ?? 0);
        }
        setConversionById(map);
        const totalReq = Number(res.totals?.total_requests ?? 0);
        const overall = Number(res.totals?.payment_overall_conversion);
        setAllConversion(totalReq > 0 && Number.isFinite(overall) ? overall : null);
      })
      .catch(() => {
        if (!cancelled) {
          setConversionById({});
          setAllConversion(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [dateFrom, dateTo]);

  const sync = useCallback(() => {
    const el = rootRef.current;
    if (!el || !open) return;
    const rect = el.getBoundingClientRect();
    const width = Math.min(420, Math.max(300, window.innerWidth - 16));
    const left = Math.min(Math.max(8, rect.right - width), window.innerWidth - width - 8);
    setBox({ top: rect.bottom + 6, left, width });
  }, [open]);

  useLayoutEffect(() => {
    if (!open) return;
    sync();
  }, [open, sync, profiles.length]);

  useEffect(() => {
    if (!open) return;
    const onMove = () => sync();
    window.addEventListener('resize', onMove);
    window.addEventListener('scroll', onMove, true);
    return () => {
      window.removeEventListener('resize', onMove);
      window.removeEventListener('scroll', onMove, true);
    };
  }, [open, sync]);

  useEffect(() => {
    if (!open) return;
    const onPointer = (event: MouseEvent) => {
      const target = event.target as Node;
      if (rootRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const canToggle = useCallback(
    (profile: VndQrProfile) => {
      if (profile.deleted_at || profile.is_archived) return false;
      if (isResponsible) return true;
      return userId != null && profile.responsible_counterparty_id === userId;
    },
    [isResponsible, userId],
  );

  const setActive = async (profile: VndQrProfile, next: boolean) => {
    if (!canToggle(profile) || busyId != null) return;
    setBusyId(profile.id);
    try {
      await cpApi.updateVndQrProfile(profile.id, { is_active: next });
      onProfilesChange(profiles.map((it) => (it.id === profile.id ? { ...it, is_active: next } : it)));
      window.dispatchEvent(new CustomEvent('vndQrProfilesChanged'));
    } catch (e) {
      const locked = enableLockedFromError(e);
      if (locked) {
        const msg =
          locked.locked_by === 'system'
            ? t('qrProfiles.enableLockedSystem')
            : locked.locked_by === 'admin'
              ? t('qrProfiles.enableLockedAdmin')
              : locked.message || t('qrProfiles.enableLocked');
        window.dispatchEvent(new CustomEvent('showToast', { detail: { message: msg, type: 'info' } }));
        window.dispatchEvent(new CustomEvent('vndQrProfilesChanged'));
        return;
      }
      if (axios.isAxiosError(e) && e.response?.status === 409) {
        const d = e.response.data as { message?: string } | string | undefined;
        const fromApi = typeof d === 'string' ? d.trim() : (d?.message || '').trim();
        window.dispatchEvent(
          new CustomEvent('showToast', {
            detail: { message: fromApi || t('qrProfiles.duplicateBankAccountActive'), type: 'error' },
          }),
        );
        window.dispatchEvent(new CustomEvent('vndQrProfilesChanged'));
        return;
      }
      window.dispatchEvent(
        new CustomEvent('showToast', {
          detail: {
            message: next ? t('qrProfiles.toggleOnError') : t('qrProfiles.toggleOffError'),
            type: 'error',
          },
        }),
      );
    } finally {
      setBusyId(null);
    }
  };

  const rows = useMemo(() => profiles.filter((p) => !p.deleted_at && !p.is_archived), [profiles]);
  const manualLabel = t('qrProfiles.modeManual');

  const menu = box ? (
    <div
      ref={menuRef}
      className="fixed z-[9999] overflow-hidden rounded-xl border border-white/15 bg-slate-900/95 shadow-xl backdrop-blur"
      style={{ top: box.top, left: box.left, width: box.width }}
    >
      <div className="max-h-80 overflow-y-auto py-1">
        <button
          type="button"
          onClick={() => {
            onChange('all');
            setOpen(false);
          }}
          className={`flex w-full items-center gap-2 px-2.5 py-1.5 text-left text-xs ${
            value === 'all' ? 'bg-blue-500/30 text-blue-100' : 'text-white/90 hover:bg-white/10'
          }`}
        >
          <span className="min-w-0 flex-1 truncate font-medium">{t('header.profileAll')}</span>
          <span className="w-[4.5rem] shrink-0 text-right font-mono tabular-nums text-white/70">
            {allConversion == null ? '—' : formatDashboardPercent(allConversion)}
          </span>
          <span className="w-[5.25rem] shrink-0" />
          <span className="w-11 shrink-0" />
        </button>
        {rows.map((profile) => {
          const conv = conversionById[profile.id];
          const lockedOff =
            !profile.is_active && profile.is_active_actor_kind && profile.is_active_actor_kind !== 'counterparty';
          return (
            <div
              key={profile.id}
              className={`flex w-full items-center gap-2 px-2.5 py-1.5 text-xs ${
                value === String(profile.id) ? 'bg-blue-500/30 text-blue-100' : 'text-white/90 hover:bg-white/10'
              } ${profile.is_active ? '' : 'opacity-70'}`}
            >
              <button
                type="button"
                onClick={() => {
                  onChange(String(profile.id));
                  setOpen(false);
                }}
                className="flex min-w-0 flex-1 items-center gap-2 text-left"
              >
                <span className="min-w-0 flex-1 truncate font-medium">{profile.name}</span>
                <span className="w-[4.5rem] shrink-0 text-right font-mono tabular-nums text-white/80">
                  {conv == null ? '—' : formatDashboardPercent(conv)}
                </span>
                <span className="w-[5.25rem] shrink-0 truncate text-[10px] font-semibold text-white/75">
                  {channelLabel(profile, manualLabel)}
                </span>
              </button>
              {canToggle(profile) ? (
                <button
                  type="button"
                  aria-label={profile.is_active ? t('qrProfiles.ariaDeactivate') : t('qrProfiles.ariaActivate')}
                  title={lockedOff ? t('qrProfiles.enableLockedHint') : undefined}
                  disabled={busyId === profile.id}
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    void setActive(profile, !profile.is_active);
                  }}
                  className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors ${
                    profile.is_active ? 'bg-emerald-500' : 'bg-white/25'
                  } ${busyId === profile.id ? 'opacity-60' : ''}`}
                >
                  <span
                    className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ${
                      profile.is_active ? 'translate-x-4' : 'translate-x-0.5'
                    }`}
                  />
                </button>
              ) : (
                <span className="w-9 shrink-0" />
              )}
            </div>
          );
        })}
      </div>
    </div>
  ) : null;

  return (
    <div ref={rootRef} className="relative w-full md:w-auto md:min-w-[160px]">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-2 rounded-lg border-0 bg-white/10 px-3 py-1.5 text-xs text-white focus:outline-none focus:ring-2 focus:ring-white/50 md:min-w-[160px]"
      >
        <span className="truncate">{triggerLabel}</span>
        <svg
          className={`h-3.5 w-3.5 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
        >
          <path
            fillRule="evenodd"
            d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 011.08 1.04l-4.25 4.512a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z"
            clipRule="evenodd"
          />
        </svg>
      </button>
      {open && box && typeof document !== 'undefined' ? createPortal(menu, document.body) : null}
    </div>
  );
}
